/** Competition store — current competition context + live state. */

import { create } from "zustand";
import { api } from "@/api/client";
import type { Competition, Division, Heat, HeatRider, LiveState, Member, Rider } from "@/types";

interface CompetitionState {
  competition: Competition | null;
  heats: Heat[];
  riders: Rider[];
  members: Member[];
  divisions: Division[];
  liveState: LiveState | null;
  currentHeatRiders: HeatRider[];
  loading: boolean;

  loadCompetition: (id: string) => Promise<void>;
  loadHeats: (competitionId: string) => Promise<void>;
  loadRiders: () => Promise<void>;
  loadMembers: (competitionId: string) => Promise<void>;
  loadLiveState: (competitionId: string) => Promise<void>;
  loadHeatRiders: (competitionId: string, heatId: string) => Promise<void>;
  loadDivisions: (competitionId: string) => Promise<void>;
  setLiveState: (state: LiveState) => void;
  reset: () => void;
}

export const useCompetitionStore = create<CompetitionState>((set) => ({
  competition: null,
  heats: [],
  riders: [],
  members: [],
  divisions: [],
  liveState: null,
  currentHeatRiders: [],
  loading: false,

  loadCompetition: async (id) => {
    set({ loading: true });
    const competition = await api.get<Competition>(`/api/competitions/${id}`);
    set({ competition, loading: false });
  },

  loadHeats: async (competitionId) => {
    const heats = await api.get<Heat[]>(`/api/competitions/${competitionId}/heats`);
    set({ heats });
  },

  loadRiders: async () => {
    const riders = await api.get<Rider[]>("/api/riders");
    set({ riders });
  },

  loadMembers: async (competitionId) => {
    const members = await api.get<Member[]>(`/api/competitions/${competitionId}/members`);
    set({ members });
  },

  loadLiveState: async (competitionId) => {
    const liveState = await api.get<LiveState>(`/api/competitions/${competitionId}/live`);
    set({ liveState });
  },

  loadHeatRiders: async (competitionId, heatId) => {
    const currentHeatRiders = await api.get<HeatRider[]>(
      `/api/competitions/${competitionId}/heats/${heatId}/riders`,
    );
    set({ currentHeatRiders });
  },

  loadDivisions: async (competitionId) => {
    const divisions = await api.get<Division[]>(`/api/competitions/${competitionId}/divisions`);
    set({ divisions });
  },

  setLiveState: (liveState) => set({ liveState }),

  reset: () =>
    set({
      competition: null,
      heats: [],
      riders: [],
      members: [],
      divisions: [],
      liveState: null,
      currentHeatRiders: [],
    }),
}));
